import mudder from 'mudder';
import qs from 'query-string';
import sanitizeHtml from 'sanitize-html';
import SDKAdapter from '../core/adapter.js';
import { SDKModel } from '../core/model.js';
import { SDKModelSchema } from '../core/schema.js';
import versionSchema from '../schemas/version.js';
import { nanoid } from '../core/transport.js';
import * as Style from '../style/index.js';
import { isDeepEquals } from '../utils/object.js';
import { DatasourceModel } from './datasources.js';
/** Tag names that must never be allowed as single-use web components (XSS risk). */
const DANGEROUS_TAGS = new Set(['script', 'iframe', 'object', 'embed']);
/**
 * Extracts custom element tag names from HTML that represent single-use web components
 * (have data-embed-src or data-embed-as). Only hyphenated tag names are considered,
 * per the custom element spec. Dangerous tags are excluded.
 */
function getSingleUseWebComponentTags(html) {
    const tagNames = new Set();
    // Match opening tags whose name contains a hyphen (custom element convention)
    const customElementRe = /<([a-zA-Z][a-zA-Z0-9]*-[a-zA-Z0-9-]*)(?:\s[^>]*)?>/g;
    let match;
    while ((match = customElementRe.exec(html)) !== null) {
        const tagName = match[1].toLowerCase();
        const fullTag = match[0];
        const hasEmbedAttrs = fullTag.includes('data-embed-src') || fullTag.includes('data-embed-as');
        if (hasEmbedAttrs && !DANGEROUS_TAGS.has(tagName)) {
            tagNames.add(tagName);
        }
    }
    return [...tagNames];
}
const allowedTags = [
    ...sanitizeHtml.defaults.allowedTags,
    'button',
    'picture',
    'img',
    'svg',
    'g',
    'path',
    'defs',
    'linearGradient',
    'stop',
    'circle',
    'style',
    'feaas-component',
    'feaas-external',
    'feaas-stylesheet'
];
const CustomAttributes = [
    'data',
    'data-attributes',
    'data-embed-html',
    'data-embed-title',
    'data-embed-src',
    'data-embed-as',
    'data-external-id',
    'component',
    'cdn',
    'version',
    'revision',
    'hostname',
    'hidden'
];
const nonBooleanAttributes = [...sanitizeHtml.defaults.nonBooleanAttributes, ...CustomAttributes];
const allowedAttributes = {
    ...sanitizeHtml.defaults.allowedAttributes,
    '*': ['class', 'style', 'id', 'data-*', ...CustomAttributes],
    path: ['d', 'fill', 'fill-rule', 'clip-rule'],
    'feaas-external': ['*'],
    'feaas-component': ['*'],
    'feaas-stylesheet': ['*']
};
export class Versions extends SDKAdapter {
    search(version, query) {
        const { libraryId } = version;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/versions?${qs.stringify(query)}`);
    }
    get(version) {
        const { libraryId, collectionId, componentId, id, status, revision } = version;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/collections/${collectionId || 'unspecified'}/components/${componentId}/versions/${id}?status=${status}&revision=${revision}`);
    }
    fetch(version) {
        const { libraryId, collectionId, componentId } = version;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/collections/${collectionId || 'unspecified'}/components/${componentId}/versions?` +
            Math.random());
    }
    post(version) {
        const { libraryId, collectionId, componentId } = version;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/collections/${collectionId || 'unspecified'}/components/${componentId}/versions`, {
            method: 'POST',
            body: JSON.stringify(version)
        });
    }
    put(version) {
        return this.post(version);
    }
    delete(version) {
        const { libraryId, collectionId, componentId, id, status, revision } = version;
        return this.sdk.fetchJSON(`/libraries/${libraryId}/collections/${collectionId || 'unspecified'}/components/${componentId}/versions/${id}?status=${status}&revision=${revision}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'text/plain' }
        });
    }
    prune({ libraryId, componentId, id } = {}) {
        // not implemented on clientside
    }
}
function getVersionDefaults(sdk) {
    var _a;
    const now = new Date();
    return {
        id: nanoid(10),
        orderIdx: '',
        revision: 0,
        createdAt: now,
        modifiedAt: now,
        deletedAt: null,
        modifiedBy: ((_a = sdk === null || sdk === void 0 ? void 0 : sdk.auth) === null || _a === void 0 ? void 0 : _a.getUser()) || {},
        datasourceIds: [],
        breakpointIds: null,
        classList: ['-theme--default'],
        view: '<section class="-section -grid--custom"></section>',
        model: '',
        diff: {},
        status: 'draft'
    };
}
export class VersionModel extends SDKModel {
    get adapter() {
        return this.sdk.Versions;
    }
    getId() {
        return this.componentId + ':' + this.id + ':' + this.revision + ':' + this.status;
    }
    getHiddenProps() {
        return ['library', 'collectionId', 'collection', 'component'];
    }
    getDefaults(sdk) {
        return getVersionDefaults(sdk);
    }
    getThumbnailURL() {
        return `${this.sdk.cdn}/thumbnails/${this.libraryId}/${this.componentId}/${this.id}.jpg`;
    }
    defineProperties() {
        super.defineProperties();
        this.sdk.utils.defineAccessor(this, 'view', {}, this.getHiddenProps());
    }
    get view() {
        return this._view;
    }
    isViewEmpty() {
        return this.view.match(/^<section[^>]*><\/section>$/i);
    }
    /** Normalizes html, extracts used datasources */
    set view(value) {
        if (typeof value == 'string') {
            value = this.normalizeView(value);
            this.datasourceIds = this.getDatasourcesFromView(value);
        }
        this._view = value;
    }
    /** Extract all datasource ids from view used in data-path-* attributes */
    getDatasourcesFromView(view) {
        const set = new Set();
        view.replace(/(data-path[a-z-]*)="([a-z0-9-_]+)[^"]*"/gi, (m, attribute, datasourceId) => {
            if (attribute != 'data-path-placeholder')
                set.add(datasourceId);
            return m;
        });
        return Array.from(set)
            .filter((v) => Boolean(v))
            .sort();
    }
    /**
     * Transform view minimally to help reduce amount of revisions. It tries to bring HTML to a canonical version, so that
     * things like different order of classes will not be considered a different version
     */
    normalizeView(view) {
        // ensure there's wrapping section element
        if (!view.match(/^[\s\n]*(<section|<style)/i) && this.id != 'responsiv') {
            view = `<section class="-section">${view}</section>`;
        }
        return this.sdk.normalizeHTML(view);
    }
    /** Combine view, classes and themes */
    getHTML(extraClasses = []) {
        return `<div class="${[...this.classList, ...extraClasses].join(' ')}">${this.view}</div>`;
    }
    /** Get latest revisions of a version matching given statuses (or first) */
    getCurrent(statuses) {
        return this.component.findVersion(this.id, [statuses || []].flat());
    }
    /** Get settings for all datasoruces used in version */
    getDataSettings() {
        return DatasourceModel.getSettingsFromDatasources(this.datasourceIds, this.sdk.datasources);
    }
    /** Get current draft or create a new one */
    getDraft() {
        const current = this.getCurrent();
        if (current.status == 'draft') {
            return current;
        }
        else {
            return current.produceNewVersion({
                ...current,
                modifiedBy: this.sdk.auth.getUser() || current.modifiedBy,
                modifiedAt: new Date(),
                revision: current.revision + 1,
                deletedAt: null,
                status: 'draft'
            });
        }
    }
    /**
     * Use current revision as a basis to create an entirely new one-off version. Used for for in-place editing of
     * components.
     */
    fork(id = this.sdk.nanoid()) {
        if (this.forkOriginId) {
            return this;
        }
        return this.produceNewVersion({
            id: id,
            forkedAt: new Date(),
            forkOriginId: this.id,
            forkOriginRevision: this.revision,
            modifiedBy: this.sdk.auth.getUser()
        });
    }
    /** Return to the shared version of a component. The fork will be marked as deleted. */
    unfork() {
        if (!this.forkOriginId) {
            return this;
        }
        return this.archive();
    }
    /** Create new version based on the current with new ID */
    produceNewVersion(data) {
        const { modifiedBy, ...rest } = this;
        return this.component.versions.add({
            ...rest,
            ...data
        });
    }
    /**
     * Creates new instance of a version revision if any data has changed.
     *
     * ! Replaces it in the collection to notify observers
     */
    changeAndNotify(data) {
        var start = data.view == null || this.compareProp('view', data.view, this.view) ? this : this.getDraft();
        const changed = start.change(data);
        if (changed != start) {
            this.component.versions.upsertItem(changed);
        }
        return changed;
    }
    /** Move version after another matching given id (or to the top if null) */
    move(anchorId, position = 'before') {
        var _a, _b;
        if (!anchorId) {
            const versions = this.component.getVersions();
            if (position == 'after') {
                anchorId = (_a = versions[0]) === null || _a === void 0 ? void 0 : _a.id;
                position = 'before';
            }
            else {
                anchorId = (_b = versions[versions.length - 1]) === null || _b === void 0 ? void 0 : _b.id;
            }
        }
        const previous = this.component.findVersion(anchorId);
        const neighbours = this.component.getNeighbourVersions(anchorId);
        const orderIdx = position == 'before'
            ? VersionModel.getVersionOrderIdx(neighbours[0], previous)
            : VersionModel.getVersionOrderIdx(previous, neighbours[1]);
        return this.getCurrent().changeAndNotify({ orderIdx });
    }
    /** Soft-delete saved version, discards draft */
    archive() {
        const draft = this.getCurrent('draft');
        const saved = this.getCurrent('saved');
        if (draft && (!saved || draft.revision > saved.revision)) {
            draft.discard();
        }
        if (saved) {
            return saved.changeAndNotify({ deletedAt: new Date() });
        }
        return this;
    }
    compareProp(property, left, right) {
        if (property == 'view' && left != null && right != null) {
            return this.normalizeView(left) == this.normalizeView(right);
        }
        return super.compareProp(property, left, right);
    }
    /** Soft-undelete version */
    restore() {
        const saved = this.getCurrent('saved') || this.getCurrent('draft');
        return saved.changeAndNotify({ deletedAt: null });
    }
    /** Set version size breakpoints and commit as new revision */
    setBreakpointIds(breakpointIds) {
        this.getDraft().changeAndNotify({ breakpointIds });
    }
    /** Creates new version with provided combo */
    setClassList(classList) {
        return this.getDraft().changeAndNotify({
            classList: classList
        });
    }
    /** Set current view/model data to version. Ensures that there is a draft, in case given data has changes */
    commitData({ view, model }) {
        if (this.error)
            return;
        const last = this.getCurrent();
        if (last.view != view || last.model != model) {
            return this.getDraft().changeAndNotify({
                modifiedBy: this.sdk.auth.getUser(),
                modifiedAt: new Date(),
                view,
                model
            });
        }
        return this;
    }
    /** Commits given data a new saved revision if there were an changes */
    saveData(data) {
        var _a;
        if (this.error)
            return;
        const last = this.getCurrent();
        if (last && last.status == 'draft' && last.deletedAt != null) {
            return;
        }
        if (!data)
            data = last;
        const { view: dirtyView, model } = data;
        // Pre-process: temporarily replace empty href with a placeholder
        const preprocessed = dirtyView.replace(/href=""/g, 'href="__EMPTY_HREF__"');
        // Allow single-use web component custom element tags that have
        // data-embed-src/data-embed-as they are not stripped by the sanitizer
        const singleUseTags = getSingleUseWebComponentTags(preprocessed);
        const allowedTagsForSave = singleUseTags.length > 0 ? [...allowedTags, ...singleUseTags] : allowedTags;
        const cleanView = sanitizeHtml(preprocessed, {
            allowedTags: allowedTagsForSave,
            nonBooleanAttributes,
            allowedAttributes,
            allowVulnerableTags: true // allow <style> tags in view which are generated by us and contain the CSS
        });
        // Post-process: restore empty href attributes
        const view = cleanView.replace(/href="__EMPTY_HREF__"/g, 'href=""');
        if (last &&
            (last.status == 'draft' ||
                last.model != model ||
                !this.compareProp('view', last.view, view) ||
                !isDeepEquals(last.breakpointIds, this.breakpointIds))) {
            last.produceNewVersion({
                view,
                model,
                breakpointIds: !((_a = this.breakpointIds) === null || _a === void 0 ? void 0 : _a.length) ? null : this.breakpointIds,
                revision: last.status == 'draft' ? last.revision : last.revision + 1,
                modifiedBy: this.sdk.auth.getUser(),
                modifiedAt: new Date(),
                status: 'saved'
            });
        }
        return this;
    }
    /** Create a separate version starting from first revision, but based on given one */
    duplicate() {
        const version = this.getCurrent();
        const neighbours = this.component.getNeighbourVersions(version.id);
        const now = new Date();
        return version.produceNewVersion({
            name: `${version.name} - Copy`,
            revision: 0,
            id: nanoid(10),
            modifiedBy: this.sdk.auth.getUser(),
            modifiedAt: now,
            createdAt: now,
            status: 'draft',
            orderIdx: VersionModel.getVersionOrderIdx(version, neighbours[1])
        });
    }
    /** Change name of version (does not create new revision) */
    rename(name) {
        this.getCurrent().changeAndNotify({ name });
    }
    setIsEmbeddable(isEmbeddable) {
        var _a, _b, _c;
        (_a = this.getCurrent('saved')) === null || _a === void 0 ? void 0 : _a.changeAndNotify({ isEmbeddable });
        (_b = this.getCurrent('staged')) === null || _b === void 0 ? void 0 : _b.changeAndNotify({ isEmbeddable });
        (_c = this.getCurrent('published')) === null || _c === void 0 ? void 0 : _c.changeAndNotify({ isEmbeddable });
    }
    /** Create a staged revision based on current one. Ensures that it's not soft-deleted */
    stage() {
        var version = this.getCurrent();
        var lastStaged = this.getCurrent('staged');
        if ((lastStaged === null || lastStaged === void 0 ? void 0 : lastStaged.revision) == version.revision) {
            return lastStaged.changeAndNotify({
                deletedAt: null,
                name: version.name,
                orderIdx: version.orderIdx
            });
        }
        else {
            return version.produceNewVersion({
                status: 'staged',
                deletedAt: null,
                modifiedBy: this.sdk.auth.getUser()
            });
        }
    }
    /** Soft-deletes staged revision */
    unstage() {
        return this.getCurrent('staged').changeAndNotify({
            deletedAt: new Date()
        });
    }
    /** Create a published revision based on current one */
    /** Ensures that it's not soft-deleted */
    publish() {
        var version = this.getCurrent();
        var lastPublished = this.getCurrent('published');
        if ((lastPublished === null || lastPublished === void 0 ? void 0 : lastPublished.revision) == version.revision) {
            return lastPublished.changeAndNotify({
                deletedAt: null,
                name: version.name,
                orderIdx: version.orderIdx
            });
        }
        else {
            return version.produceNewVersion({
                status: 'published',
                deletedAt: null,
                modifiedBy: this.sdk.auth.getUser()
            });
        }
    }
    /** Soft-deletes published revision */
    unpublish() {
        return this.getCurrent('published').changeAndNotify({
            deletedAt: new Date()
        });
    }
    /** Discards the draft, removes it from revision list */
    discard() {
        if (this.status != 'draft') {
            throw new Error('Only drafts can be discarded');
        }
        this.component.versions.removeItem(this);
        return this;
    }
    /** Revert current changes to last published/staged one */
    revertTo(revision) {
        const draft = this.getCurrent('draft');
        if (draft) {
            draft.discard();
        }
        const saved = this.getCurrent('saved');
        if (!revision.deletedAt && saved.revision > revision.revision) {
            return saved.getDraft().changeAndNotify(revision.toProperties());
        }
    }
    toProperties() {
        return {
            view: this.view,
            model: this.model,
            breakpointIds: this.breakpointIds
        };
    }
    revert() {
        const draft = this.getCurrent('draft');
        const saved = this.getCurrent('saved');
        if (!draft && !saved)
            return;
        const published = this.getCurrent('published');
        const staged = this.getCurrent('staged');
        if ((published === null || published === void 0 ? void 0 : published.revision) >= (staged === null || staged === void 0 ? void 0 : staged.revision)) {
            this.revertTo(published);
        }
        else if (staged) {
            this.revertTo(staged);
        }
        else {
            return draft.changeAndNotify({
                view: '',
                model: ''
            });
        }
    }
    isDifferentFrom(other) {
        var _a, _b, _c, _d;
        return (this.name !== other.name ||
            this.description !== other.description ||
            this.isEmbeddable !== other.isEmbeddable ||
            this.status !== other.status ||
            ((_a = this.classList) === null || _a === void 0 ? void 0 : _a.slice().join(',')) !== ((_b = other.classList) === null || _b === void 0 ? void 0 : _b.slice().join(',')) ||
            this.orderIdx !== other.orderIdx ||
            ((_c = this.datasourceIds) === null || _c === void 0 ? void 0 : _c.slice().sort().join(',')) !== ((_d = other.datasourceIds) === null || _d === void 0 ? void 0 : _d.slice().sort().join(',')) ||
            String(this.deletedAt) !== String(other.deletedAt));
    }
    getBreakpoints() {
        var _a;
        const stylesBreakpoints = this.library.stylesheet.rules.filter((s) => s.type == 'breakpoint');
        return (((_a = this.breakpointIds) === null || _a === void 0 ? void 0 : _a.length)
            ? stylesBreakpoints.filter((breakpoint) => this.breakpointIds.includes(breakpoint.details.id))
            : stylesBreakpoints).filter(Boolean);
    }
    getDatasources() {
        return this.datasourceIds
            .map((datasourceId) => this.sdk.datasources.find((d) => d.id == datasourceId))
            .filter(Boolean);
    }
    matchBreakpoint(breakpoint, targetWidth) {
        return targetWidth >= breakpoint.props.minWidth && targetWidth <= breakpoint.props.maxWidth;
    }
    matchesSearch(search) {
        return this.name.toLowerCase().includes(search.toLowerCase());
    }
    static getOrderedBreakpoints(targetWidth, breakpointsArray) {
        const breakpoints = [...breakpointsArray];
        const getBreakpointScore = (b) => 
        // find distance to closest boundary
        Math.min(Math.abs(targetWidth - b.props.minWidth), Math.abs(targetWidth - b.props.maxWidth)) +
            // prefer breakpoints that fit within the screen
            (b.props.minWidth <= targetWidth ? 0 : 1000);
        return breakpoints.sort((a, b) => getBreakpointScore(a) - getBreakpointScore(b));
    }
    getMatchingBreakpoint(targetWidth) {
        const breakpoints = this.getBreakpoints();
        // find corresponding breakpoint for window width
        let match = breakpoints.find((b) => this.matchBreakpoint(b, targetWidth));
        if (match) {
            return match;
        }
        // if no breakpoints for current window width, select the closest one
        return VersionModel.getOrderedBreakpoints(targetWidth, breakpoints)[0];
    }
    getUsedComboIds() {
        return this.classList.map((className) => className.replace('-use--', ''));
    }
    getLastPushed() {
        const published = this.getCurrent('published');
        const staged = this.getCurrent('staged');
        return (published === null || published === void 0 ? void 0 : published.revision) > (staged === null || staged === void 0 ? void 0 : staged.revision) ? published : staged;
    }
    needsStaging() {
        const staged = this.getCurrent('staged');
        const current = this.getCurrent();
        return !staged || current.revision > staged.revision || !!staged.deletedAt;
    }
    needsPublishing() {
        const published = this.getCurrent('published');
        const current = this.getCurrent();
        return !published || current.revision > published.revision || !!published.deletedAt;
    }
    isDeleted() {
        var _a;
        return ((_a = (this.getCurrent('saved') || this.getCurrent('draft'))) === null || _a === void 0 ? void 0 : _a.deletedAt) != null;
    }
    getThemeSlug() {
        return Style.ClassList.getThemeSlug(this.classList);
    }
    toStyleContext() {
        return {
            name: 'version',
            getAttribute: (name) => {
                if (name == 'class') {
                    return this.classList.join(' ');
                }
                return null;
            }
        };
    }
    static getOrderedVersions(versions) {
        return this.reduceVersionRevisions(versions)
            .map((snapshot) => snapshot.head)
            .sort((a, b) => a.sortCompare(b));
    }
    static getVersionOrderIdx(left, right) {
        const leftIdx = (left === null || left === void 0 ? void 0 : left.orderIdx) || '';
        const rightIdx = (right === null || right === void 0 ? void 0 : right.orderIdx) || '';
        return mudder.base62.mudder(leftIdx, rightIdx, 1, undefined, 1000, 4)[0] || '';
    }
    // use orderIdx string for sorting
    sortCompare(other) {
        return this.orderIdx > other.orderIdx ? 1 : this.orderIdx < other.orderIdx ? -1 : 0;
    }
    /** Find all unique in html and generate new ones, fix references */
    static getUniqueHTML(view, map = {}) {
        var replaced = view.replace(/(data-symbol-id|data-instance-id)="([^"]+?)"/g, (m, attribute, id) => {
            map[id] || (map[id] = nanoid(10));
            return `${attribute}="${map[id]}"`;
        });
        return Object.keys(map).reduce((current, id) => {
            while (true) {
                const replaced = current
                    .replace(new RegExp(`"${id}"`), `"${map[id]}"`)
                    .replace(new RegExp(`Id: ${id}`), `Id: ${map[id]}`);
                if (replaced == current) {
                    break;
                }
                current = replaced;
            }
            return current;
        }, replaced);
    }
    static sortVersionRevisions(revisions) {
        return revisions.sort((a, b) => {
            return (a.id.localeCompare(b.id) ||
                Number(b.revision) - Number(a.revision) /** Higher revision first */ ||
                VersionModel.statuses.indexOf(a.status) -
                    VersionModel.statuses.indexOf(b.status) /** Published first, draft last */ ||
                Number(a.createdAt) - Number(b.createdAt));
        });
    }
    static reduceVersionRevisions(revisions) {
        return this.sortVersionRevisions(revisions)
            .reduce((snapshots, revision) => {
            var snapshot = snapshots.find((snapshot) => snapshot.id == revision.id);
            if (!snapshot) {
                snapshot = { id: revision.id, revisions: [] };
                snapshots.push(snapshot);
            }
            snapshot.revisions.push(revision);
            if (!snapshot[revision.status])
                snapshot[revision.status] = revision;
            if (!snapshot.head)
                snapshot.head = revision;
            if (revision.status != 'draft' && !snapshot.previous)
                snapshot.previous = revision;
            return snapshots;
        }, [])
            .sort((a, b) => a.head.sortCompare(b.head));
    }
    static onCollectionChange(versions) {
        return this.sortVersionRevisions(versions);
    }
    getPath() {
        return `/libraries/${this.libraryId}/collections/${this.collectionId}/components/${this.componentId}/versions/${this.id}`;
    }
    getSourcePath() {
        return `${this.libraryId}/${this.componentId}/${this.id}/${this.status}.html`;
    }
    static get schema() {
        return new SDKModelSchema(VersionModel, versionSchema);
    }
}
VersionModel.statuses = ['published', 'staged', 'saved', 'draft'];
//# sourceMappingURL=versions.js.map